import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  ListItemIcon,
  Alert,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircleOutlineOutlined,
  AccessTime,
  ErrorOutlineOutlined,
  SaveOutlined,
  SendOutlined,
  PictureAsPdf,
  DescriptionOutlined,
  Close,
  PhotoLibraryOutlined,
  AssignmentOutlined,
  Compare,
  EditOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { Project } from '../types';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { defaultRetryPolicy } from '../utils/retryPolicy';
import { useAuth } from '../contexts/AuthContext';
import { getPhotoGroups } from '../config/zones';
import { captureException } from '../utils/captureException';

interface FinishProps extends WithDeviceProps {
  project: Project;
  openFinish: boolean;
  handleClose: () => void;
  handleNextStep: (id: string) => void;
  onOpenPdf: () => void;
  setPdfType: (pdfType: string) => void;
  isSmallScreen: boolean;
  refetch: () => void;
  handleCloseAll: () => void;
}

// Add this component before the Identification component (around line 61)
const NoTransition = React.forwardRef<any, any>(
  function NoTransition(props, ref) {
    const {
      children,
      appear,
      in: inProp,
      timeout,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      ownerState,
      ...rest
    } = props;
    // Return children directly without wrapper to preserve Dialog's layout
    return <>{children}</>;
  }
);

const Finish: React.FC<FinishProps> = ({
  project,
  openFinish,
  handleClose,
  handleNextStep,
  onOpenPdf,
  setPdfType,
  isSmallScreen,
  refetch,
  handleCloseAll,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [zone_ids, setZoneIds] = useState<Record<string, string[]>>({});
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [comments, setComments] = useState<string>('');
  const [drafting, setDrafting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const isEditable = project?.editable !== false;

  // Create zone_ids object from zones.ts data
  const createZoneIds = (): Record<string, string[]> => {
    const photoGroups = getPhotoGroups(t);

    const zoneIdsObject: Record<string, string[]> = {};

    photoGroups.forEach((group) => {
      zoneIdsObject[group.id] = group.photos.map((photo) => photo.id);
    });

    return zoneIdsObject;
  };

  useEffect(() => {
    const zoneIds = createZoneIds();
    setZoneIds(zoneIds);
  }, [t]);

  useEffect(() => {
    if (project?.comments !== undefined) {
      setComments(project.comments || '');
    }
  }, [project]);

  const generatePDF = async () => {
    setProcessing(true);
    setError(null);
    try {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/generate/pdf`;
      const response = await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.post(apiUrl, { 
          project_code: project?.project_code,
          photo_zone_ids: zone_ids,
         }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Generate PDF API call', {
        apiUrl,
        projectCode: project?.project_code,
      });
      console.log("pdf generated", response.data);
      setSuccess(true);
      refetch();
    } catch (error) {
      captureException(error, { tags: { feature: 'finish' }, extra: { action: 'generatePdf', project_code: project?.project_code } });
      console.error('Error generating PDF:', error);
      setError((error as any)?.response?.data?.details || (error as any)?.response?.data?.error || (error as any)?.message || (error as Error).message || 'Error generating PDF');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        setSuccess(false);
      }, 8000);
    }
  }, [success]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      return new Intl.DateTimeFormat('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(dateString));
    } catch {
      return null;
    }
  };

  const saveComments = async () => {
    try {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/project/${project?.project_code}`;
      await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.put(apiUrl, {
          comments: comments,
          user_comments: user?.uid,
        }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Save comments API call', {
        apiUrl,
        projectCode: project?.project_code,
      });
      await refetch();
    } catch (error) {
      captureException(error, { tags: { feature: 'finish' }, extra: { action: 'saveComments', project_code: project?.project_code } });
      console.error('Error saving comments:', error);
      setError((error as any)?.response?.data?.error || (error as any)?.message || 'Error saving comments');
    }
  };

  const saveFinishParams = async () => {
    try {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/project/${project?.project_code}`;
      await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.put(apiUrl, {
          editable: false,
          status: 'completed'
        }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Save comments API call', {
        apiUrl,
        projectCode: project?.project_code,
      });
      await refetch();
    } catch (error) {
      captureException(error, { tags: { feature: 'finish' }, extra: { action: 'saveFinishParams', project_code: project?.project_code } });
      console.error('Error saving comments:', error);
      setError((error as any)?.response?.data?.error || (error as any)?.message || 'Error saving comments');
    }
  };

  const sendProject = async () => {
    try {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/finish/send/${project?.project_code}`;
      const response = await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.post(apiUrl, {
          project_code: project?.project_code,
        }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Send project API call', {
        apiUrl,
        userId: user?.uid, 
        projectCode: project?.project_code,
      });
      console.log("send project", response.data);
      await refetch();
    } catch (error) {
      captureException(error, { tags: { feature: 'finish' }, extra: { action: 'sendProject', project_code: project?.project_code } });
      console.error('Error saving comments:', error);
      setError((error as any)?.response?.data?.error || (error as any)?.message || 'Error saving comments');
    }
  };

  const handleSaveDraft = async () => {
    setDrafting(true);
    try {
      await saveComments();
      handleNextStep('finalize-inspection');
    } finally {
      setDrafting(false);
    }
  };

  const handleFinishAndSend = async () => {
    setFinishing(true);
    try {
      await saveComments();
      await saveFinishParams();
      await sendProject();
      navigate('/');
    } catch (error) {
      captureException(error, { tags: { feature: 'finish' }, extra: { action: 'handleFinishAndSend', project_code: project?.project_code } });
      console.error('Error finishing and sending project:', error);
      setError((error as any)?.response?.data?.error || (error as any)?.message || 'Error finishing and sending project');
      setFinishing(false);
    }
  };

  const handleEnableEditing = async () => {
    try {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/project/${project?.project_code}`;
      await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.put(apiUrl, {
          editable: true,
          status: 'pending'
        }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Save comments API call', {
        apiUrl,
        projectCode: project?.project_code,
      });
      await refetch();
    } catch (error) {
      captureException(error, { tags: { feature: 'finish' }, extra: { action: 'handleEnableEditing', project_code: project?.project_code } });
      console.error('Error saving comments:', error);
      setError((error as any)?.response?.data?.error || (error as any)?.message || 'Error saving comments');
    }
  };

  return (
    <Dialog
      fullScreen
      open={openFinish && project !== null}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        pt: isSmallScreen ? "56px" : "64px",
        pb: "56px",
      }}
      onClose={handleClose}
      slots={{ transition: NoTransition }}
      key={"finish"}
    >
      <DialogTitle sx={{ p: 0, bgcolor: 'background.light' }}>
        <AppBar
          color="inherit"
          position="fixed"
          sx={{
            pt: isSmallScreen ? "56px" : "64px",
          }}
        >
          <Toolbar sx={{ p: 1 }}>
            <IconButton onClick={handleClose} color="inherit" sx={{ mr: 1 }}><ArrowBack/></IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }} />
            {/* <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('finish.title')}</Typography> */}
            {project?.gcs_series_book_path && (
              <Tooltip title={t('projectList.seriesBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('series');
                }}
                sx={{ mr: 0.5 }}><Compare /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_homologation_book_path && (
              <Tooltip title={t('projectList.homologationBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('homologation');
                }}
                sx={{ mr: 0.5 }}><AssignmentOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_image_book_path && (
              <Tooltip title={t('projectList.imageBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('image_book');
                }}
                sx={{ mr: 0.5 }}><PhotoLibraryOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_report_path && (
              <Tooltip title={t('projectList.reportTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('report');
                }}
                sx={{ mr: 0.5 }}><DescriptionOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_pdf_path && (
              <Tooltip title={t('projectList.launchTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('lanzamiento');
                }}
                sx={{ mr: 0.5 }}><PictureAsPdf /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleCloseAll} color="inherit"><Close /></IconButton>
          </Toolbar>
        </AppBar>
        {project && (
          <Box sx={{ py: 3, pl: 2, pr: 2, minHeight: "calc(100vh - 112px - " + (isSmallScreen ? "56px" : "72px") + ")", mt: isSmallScreen ? "56px" : "64px" }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, pb: 0, pt: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.07)',}}>
                <ListItem>
                  <ListItemText primary={t('finish.project') + " " + project.project_code} secondary={formatDate(project.lastModifiedAt)} />
                </ListItem>
              </List>
              <Paper 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  p: 2, 
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  gap: 2,
                }}
                onClick={() => handleNextStep('init')}
              >
                {project.status_inspeccion === "completed" ? <CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /> : <AccessTime sx={{ color: 'warning.main' }} />}
                <Typography variant="h6" gutterBottom sx={{ fontSize: 16, fontWeight: 500, mb: 0 }}>{t('finish.vehicleIdentification')}</Typography>
              </Paper>
              <Paper 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  gap: 2,
                }}
                onClick={() => handleNextStep('vehicle-identification')}
              >
                {project.status_fotos === "completed" ? <CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /> : <AccessTime sx={{ color: 'warning.main' }} />}
                <Typography variant="h6" gutterBottom sx={{ fontSize: 16, fontWeight: 500, mb: 0 }}>{t('finish.vehiclePhotos')}</Typography>
              </Paper>
              <Paper 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  p: 2, 
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  gap: 2,
                }}
                onClick={() => handleNextStep('image-capture')}
              >
                {project.status_opcionales === "completed" ? <CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /> : <AccessTime sx={{ color: 'warning.main' }} />}
                <Typography variant="h6" gutterBottom sx={{ fontSize: 16, fontWeight: 500, mb: 0 }}>{t('finish.optionalReview')}</Typography>
              </Paper>
              <Paper 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  gap: 2,
                }}
                onClick={() => handleNextStep('vehicle-options')}
              >
                {project.status_mediciones === "completed" ? <CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /> : <AccessTime sx={{ color: 'warning.main' }} />}
                <Typography variant="h6" gutterBottom sx={{ fontSize: 16, fontWeight: 500, mb: 0 }}>{t('finish.measurements')}</Typography>
              </Paper>
              <Paper 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  gap: 2,
                }}
                onClick={() => handleNextStep('measurements')}
              >
                {project.status_series === "completed" ? <CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /> : <AccessTime sx={{ color: 'warning.main' }} />} 
                <Typography variant="h6" gutterBottom sx={{ fontSize: 16, fontWeight: 500, mb: 0 }}>{t('finish.seriesComparison')}</Typography>
              </Paper>
              {(project.warning_inspeccion?.filter((warning) => warning.check === false).length > 0 ||
                project.warning_fotos?.filter((warning) => warning.check === false).length > 0 ||
                project.warning_opcionales?.filter((warning) => warning.check === false).length > 0 ||
                project.warning_mediciones?.filter((warning) => warning.check === false).length > 0 ||
                project.warning_series?.filter((warning) => warning.check === false).length > 0) && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mt: 2,
                  fontSize: 18,
                }}
              >
                {t('finish.problemsFound')}
              </Typography>
              )}
              {project.warning_inspeccion?.filter((warning) => warning.check === false).length > 0 && (
                <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, pb: 0, pt: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.07)',}}>
                  <ListItem>
                    <ListItemIcon>
                      <ErrorOutlineOutlined sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    {/* secondary text should be in separated lines */}
                    <ListItemText
                      primary={t('finish.inspectionCount', { count: project.warning_inspeccion?.filter((warning) => warning.check === false).length })}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={project.warning_inspeccion?.filter((warning) => warning.check === false).map((warning) => (
                        <Typography key={warning.id} variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                          {"- " + warning.message}
                        </Typography>
                      ))}
                    />
                  </ListItem>
                </List>
              )}
              {project.warning_fotos?.filter((warning) => warning.check === false).length > 0 && (
                <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, pb: 0, pt: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.07)',}}>
                  <ListItem>
                    <ListItemIcon>
                      <ErrorOutlineOutlined sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    {/* secondary text should be in separated lines */}
                    <ListItemText
                      primary={t('finish.fotosIncompletasCount', { count: project.warning_fotos?.filter((warning) => warning.check === false).length })}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={project.warning_fotos?.filter((warning) => warning.check === false).map((warning) => (
                        <Typography key={warning.id} variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                          {"- " + warning.message}
                        </Typography>
                      ))}
                    />
                  </ListItem>
                </List>
              )}
              {project.warning_opcionales?.filter((warning) => warning.check === false).length > 0 && (
                <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, pb: 0, pt: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.07)',}}>
                  <ListItem>
                    <ListItemIcon>
                      <ErrorOutlineOutlined sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    {/* secondary text should be in separated lines */}
                    <ListItemText
                      primary={t('finish.opcionalesIncompletosCount', { count: project.warning_opcionales?.filter((warning) => warning.check === false).length })}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={project.warning_opcionales?.filter((warning) => warning.check === false).map((warning) => (
                        <Typography key={warning.id} variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                          {"- " + warning.message}
                        </Typography>
                      ))}
                    />
                  </ListItem>
                </List>
              )}
              <TextField
                multiline
                rows={4}
                fullWidth
                label={t('finish.comments')}
                value={comments}
                disabled={!(project.editable !== false)}
                onChange={(e) => setComments(e.target.value)}
                variant="outlined"
                sx={{ mt: 2, bgcolor: 'background.paper' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button startIcon={<SaveOutlined />} variant="outlined" color="primary" size="large" onClick={handleSaveDraft} disabled={drafting || processing || finishing || !(project.editable !== false)}>{t('finish.saveDraft')}</Button>
                <Button startIcon={<DescriptionOutlined />} variant="outlined" color="primary" size="large" onClick={() => generatePDF()} disabled={drafting || processing || finishing || !(project.editable !== false)}>{t('finish.generatePDF')}</Button>
                {(isEditable || finishing) && <Button 
                  // disabled={!(project.status_inspeccion === "completed" && project.status_fotos === "completed" && project.status_opcionales === "completed" && project.status_mediciones === "completed" && project.status_series === "completed")}
                  disabled={drafting || processing || finishing || !(project.editable !== false)}
                  startIcon={<SendOutlined />} 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  onClick={handleFinishAndSend}>{t('finish.finishAndSend')}</Button>}
                {!isEditable && !finishing && <Button 
                  // disabled={!(project.status_inspeccion === "completed" && project.status_fotos === "completed" && project.status_opcionales === "completed" && project.status_mediciones === "completed" && project.status_series === "completed")}
                  disabled={drafting || processing}
                  startIcon={<EditOutlined />} 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  onClick={handleEnableEditing}>{t('finish.enableEditing')}</Button>}
              </Box>
            </Box>
          </Box>
        )}
      </DialogTitle>
      {error && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 202, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {error}
          <IconButton onClick={() => setError(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 201, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {t('finish.pdfGeneratedSuccessfully')}
          <IconButton onClick={() => setSuccess(false)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
      {processing && (
        <Alert severity="info" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 200, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {t('finish.processing')}
        </Alert>
      )}
    </Dialog>
  );
};

export default withDevice(React.memo(Finish));
